/**
 * Clinician assignment / permission helpers.
 *
 * These pure functions resolve who can edit which assessment stage from the
 * patient's `clinicians` map (and, for SSI, from an optional Lead override).
 *
 * Lead SSI override (SLaM / 3 Borough)
 * ------------------------------------
 * When `dataModel.3_semiStructuredInterview.leadOverride.active` is true, the
 * CURRENT Lead clinician becomes the effective owner of SSI editing and AI
 * paragraph review. The original `clinicians.clinicianSSI` slot is deliberately
 * left untouched so report attribution and the original appointment date stay
 * with the clinician who first completed the SSI.
 *
 * Ownership follows the Lead *role* (not the individual who pressed Override),
 * so a later Lead reassignment transfers the override with the role.
 */

/**
 * Checks if the current clinician is the Lead clinician for a patient
 * @param {Object} patientData - The patient data model
 * @param {string} clinicianId - The ID of the current clinician
 * @returns {boolean} - Whether the clinician is the Lead clinician
 */
export const isLeadClinician = (patientData, clinicianId) => {
    if (!patientData || !clinicianId) return false;
    return patientData.clinicians?.clinicianLead?.clinicianId === clinicianId;
};

/**
 * Whether a Lead SSI override is currently active on this patient.
 * Null-safe: missing/malformed override data is treated as inactive.
 *
 * @param {Object|null} patientData
 * @returns {boolean}
 */
export const isSSILeadOverrideActive = (patientData) => {
    return patientData?.dataModel?.['3_semiStructuredInterview']?.leadOverride?.active === true;
};

/**
 * Returns the clinician who currently owns SSI editing / AI-paragraph review.
 *
 * When a Lead override is active, ownership follows the CURRENT Lead role
 * (so a later Lead reassignment transfers the override). Otherwise it is the
 * assigned `clinicianSSI` slot.
 *
 * @param {Object|null} patientData
 * @returns {{ clinicianId: string|null, clinicianName: string|null, isOverridden: boolean }}
 */
export const getEffectiveSSIClinician = (patientData) => {
    const leadOverride = patientData?.dataModel?.['3_semiStructuredInterview']?.leadOverride;
    const overrideActive = leadOverride?.active === true;

    if (overrideActive) {
        const lead = patientData?.clinicians?.clinicianLead;
        return {
            clinicianId: lead?.clinicianId || null,
            clinicianName: lead?.clinicianName || null,
            isOverridden: true,
        };
    }

    const ssi = patientData?.clinicians?.clinicianSSI;
    return {
        clinicianId: ssi?.clinicianId || null,
        clinicianName: ssi?.clinicianName || null,
        isOverridden: false,
    };
};

/**
 * Checks whether the clinician is the original (slot) SSI assignee — i.e. the
 * person recorded on `clinicians.clinicianSSI`, ignoring any Lead override.
 *
 * Used by pages that must remain the original clinician's personal record
 * even after an override (SSI Review & Synthesis / Confidential Case Notes,
 * and the legacy RDD page).
 *
 * @param {Object|null} patientData
 * @param {string|null} clinicianId
 * @returns {boolean}
 */
export const isOriginalSSIClinician = (patientData, clinicianId) => {
    if (!patientData || !clinicianId) return false;
    return patientData.clinicians?.clinicianSSI?.clinicianId === clinicianId;
};

/**
 * Checks if the current clinician is assigned to a specific stage for a patient
 * @param {Object} patientData - The patient data model
 * @param {string} clinicianId - The ID of the current clinician
 * @param {string} stage - The stage to check (CI, SSI, DD, PAD)
 * @returns {boolean} - Whether the clinician is assigned to the stage
 */
export const isClinicianAssignedToStage = (patientData, clinicianId, stage) => {
    if (!patientData || !clinicianId || !stage) return false;
    
    // Map stage abbreviations to the full property names in the clinicians object
    const stagePropertyMap = {
      'CI': 'clinicianCI',
      'SSI': 'clinicianSSI',
      'DD': 'clinicianDD',
      'PAD': 'clinicianPAD',
      'OBS': 'clinicianOBS',
    };
    
    const propertyName = stagePropertyMap[stage];
    if (!propertyName) return false;

    // SSI with an active Lead override: ownership follows the current Lead.
    // The original clinicianSSI is demoted to read-only across the live SSI
    // (Symptoms, Domains, Review & Synthesis) and Documents Dashboard review.
    // Their personal Emerging Formulation / Confidential Notes are preserved
    // in the ssiOverrideArchive snapshot, not as a live-edit carve-out.
    if (stage === 'SSI' && isSSILeadOverrideActive(patientData)) {
      return patientData.clinicians?.clinicianLead?.clinicianId === clinicianId;
    }
    
    // Check if this clinician is assigned to this stage
    const assignedClinician = patientData.clinicians?.[propertyName];
    
    // Check if current clinician is the assigned clinician
    if (assignedClinician?.clinicianId === clinicianId) return true;
    
    // NOTE: DD supporting clinicians (MDT review participants) are intentionally
    // excluded here. They can VIEW DD pages but should NOT get canEdit = true,
    // because they do not make diagnostic decisions, finish sections, or
    // approve AI report paragraphs. That remains the Lead clinician's job.
    // Their patient-level access is handled by isClinicianAssignedToPatient.
    
    return false;
  };
  
/**
 * Checks if a clinician has any assignment to a patient (even without stage-specific permissions)
 * @param {Object} patientData - The patient data model
 * @param {string} clinicianId - The ID of the current clinician
 * @returns {boolean} - Whether the clinician has access to the patient
 */
export const isClinicianAssignedToPatient = (patientData, clinicianId) => {
    if (!patientData || !clinicianId) return false;
    
    const stageProperties = ['clinicianCI', 'clinicianSSI', 'clinicianDD', 'clinicianPAD', 'clinicianLead', 'clinicianOBS'];
    
    // Check if clinician is assigned to any stage or as lead
    for (const prop of stageProperties) {
      const assignedClinician = patientData.clinicians?.[prop];
      
      if (assignedClinician?.clinicianId === clinicianId) {
        return true;
      }
      
      // NOTE: DD supporting clinicians (MDT review participants) are intentionally
      // excluded. They get read-only access to all modules (same as an unassigned
      // Support / Org Admin). Their navigation and dashboard access is handled by
      // PatientDataContext and patient_summaries.assignedClinicianIds respectively,
      // not by this function.
      // if (prop === 'clinicianDD' && assignedClinician?.supportingClinicianIds) {
      //   if (assignedClinician.supportingClinicianIds.includes(clinicianId)) {
      //     return true;
      //   }
      // }
    }
    
    return false;
  };
